import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Typography, Stack, Button, TextField, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Paper, Chip, CircularProgress, IconButton,
  Tooltip, InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { bgvApi } from '../../../api/bgvApi';
import {
  BgvCaseDto,
  BgvCaseStatusLabels,
  BgvCaseStatusColors,
  BgvFinalDecisionLabels,
  BgvFinalDecisionColors,
} from '../../../types/bgvTypes';
import { RootState } from '../../../store/store';

const BgvVerificationListPage: React.FC = () => {
  const navigate = useNavigate();
  const roles = useSelector((state: RootState) => state.auth.user?.roles ?? []);
  const permissions = useSelector((state: RootState) => state.auth.user?.permissions ?? []);
  const isBgvVendorRole = roles.some((role) => role.toLowerCase() === 'bgvvendor');
  const isTenantAdmin = roles.some((role) => {
    const normalizedRole = role.toLowerCase();
    return normalizedRole.includes('admin') && normalizedRole !== 'superadmin';
  });
  const canManageBgv = (permissions.includes('ManageBGV') || isTenantAdmin) && !isBgvVendorRole;
  const isVendorPortal = permissions.includes('ViewAssignedBGV') && !canManageBgv;
  const [cases, setCases] = useState<BgvCaseDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [decisionFilter, setDecisionFilter] = useState<string>('');

  const loadCases = useCallback(async () => {
    setLoading(true);
    try {
      const filters: Record<string, any> = {};
      if (searchTerm) filters.searchTerm = searchTerm;
      if (statusFilter !== '') filters.status = Number(statusFilter);
      if (decisionFilter !== '') filters.finalDecision = Number(decisionFilter);

      const data = isVendorPortal
        ? await bgvApi.getAssignedCases(filters)
        : await bgvApi.getCases(filters);

      setCases(data);
    } catch {
      toast.error('Failed to load verification cases');
    } finally {
      setLoading(false);
    }
  }, [decisionFilter, isVendorPortal, searchTerm, statusFilter]);

  useEffect(() => {
    void loadCases();
  }, [loadCases]);

  const detailPathFor = (id: string) => isVendorPortal ? `/bgv/assigned-cases/${id}` : `/bgv/cases/${id}`;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>
          {isVendorPortal ? 'Assigned Background Verification Cases' : 'Background Verification Cases'}
        </Typography>
        {!isVendorPortal && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/bgv/create')}
            sx={{ textTransform: 'none' }}
          >
            New Verification
          </Button>
        )}
      </Stack>

      <Stack direction="row" spacing={2} mb={3} flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          placeholder="Search by name, code, or case #..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          {Object.entries(BgvCaseStatusLabels).map(([key, label]) => (
            <MenuItem key={key} value={key}>{label}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Decision"
          value={decisionFilter}
          onChange={(e) => setDecisionFilter(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All Decisions</MenuItem>
          {Object.entries(BgvFinalDecisionLabels).map(([key, label]) => (
            <MenuItem key={key} value={key}>{label}</MenuItem>
          ))}
        </TextField>
        <Tooltip title="Refresh">
          <IconButton onClick={loadCases}><RefreshIcon /></IconButton>
        </Tooltip>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Total Records: {cases.length}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Case #</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Employee Code</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Decision</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Assigned To</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Progress</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cases.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No verification cases found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  cases.map((c) => (
                    <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(detailPathFor(c.id))}>
                      <TableCell><Typography fontWeight={600}>{c.caseNumber}</Typography></TableCell>
                      <TableCell>{c.employeeName}</TableCell>
                      <TableCell>{c.employeeCode}</TableCell>
                      <TableCell>
                        <Chip
                          label={BgvCaseStatusLabels[c.status]}
                          color={BgvCaseStatusColors[c.status]}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={BgvFinalDecisionLabels[c.finalDecision]}
                          color={BgvFinalDecisionColors[c.finalDecision]}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{c.vendorName || c.assignedToName || '-'}</TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {c.completedTasks}/{c.totalTasks} tasks
                        </Typography>
                      </TableCell>
                      <TableCell>{new Date(c.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(detailPathFor(c.id));
                            }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default BgvVerificationListPage;
